import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  { id: 'kohima', name: 'Kohima', homestays: 280, valleys: ['War Cemetery', 'Dzükou Valley', 'Japfü Peak', 'Khonoma Village'], altitude: '1,400–3,048m', highlight: true, division: 'Southern' },
  { id: 'dimapur', name: 'Dimapur', homestays: 200, valleys: ['Kachari Ruins', 'Rangapahar WLS', 'Triple Falls', 'Intanki NP'], altitude: '200–800m', highlight: true, division: 'Western' },
  { id: 'mokokchung', name: 'Mokokchung', homestays: 180, valleys: ['Longkhum', 'Ungma Village', 'Mopungchuket', 'Changtongya'], altitude: '1,200–2,000m', highlight: true, division: 'Central' },
  { id: 'mon', name: 'Mon', homestays: 160, valleys: ['Longwa Village', 'Shangnyu', 'Konyak Morung', 'Chi Ruins'], altitude: '800–2,500m', highlight: true, division: 'Northern' },
  { id: 'ukhrul', name: 'Wokha', homestays: 120, valleys: ['Doyang River', 'Totsu Cliff', 'Mt Tiyi', 'Lotha Villages'], altitude: '1,000–2,500m', highlight: true, division: 'Central' },
  { id: 'phek', name: 'Phek', homestays: 100, valleys: ['Dzükou Valley Trail', 'Shilloi Lake', 'Glory Peak', 'Chakhesang Villages'], altitude: '1,500–2,994m', highlight: false, division: 'Southern' },
  { id: 'zunheboto', name: 'Zunheboto', homestays: 80, valleys: ['Satoi Range', 'Ghoshu Bird Sanctuary', 'Sumi Villages', 'Aopao'], altitude: '1,000–2,200m', highlight: false, division: 'Central' },
  { id: 'tuensang', name: 'Tuensang', homestays: 70, valleys: ['Chang Villages', 'Hakchang', 'Changsangmonko', 'Eastern Ridges'], altitude: '800–2,300m', highlight: false, division: 'Eastern' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Nagaland Tourism Department, across 16 tribal territories and ancient village republics, digitized on one platform.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on tribe map', 'Basic contact listing', 'Searchable by tribe & district'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified. Verified morung stays, warrior-village retreats, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The hidden treasures of warrior country.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay from Kohima\'s ridges to Mon\'s Konyak longhouses — searchable, verified, bookable on a single platform.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, Hornbill Festival trends, and tribal-tourism season insights for Nagaland tourism planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy — empowering 16 warrior tribes to share their traditions, feasts, and mountain villages with the world.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification ensuring each homestay honours the ancient village-republic governance, morung traditions, and communal feasting culture.' },
];

function NagalandMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'mon': { d: 'M100 30 L220 20 L260 65 L240 115 L170 125 L110 95 L80 60 Z', cx: 170, cy: 72 },
    'tuensang': { d: 'M240 115 L260 65 L370 55 L390 100 L380 150 L310 160 L260 145 Z', cx: 315, cy: 110 },
    'mokokchung': { d: 'M110 95 L170 125 L240 115 L260 145 L230 185 L170 190 L120 160 Z', cx: 185, cy: 150 },
    'dimapur': { d: 'M30 130 L80 60 L110 95 L120 160 L90 200 L40 185 Z', cx: 80, cy: 140 },
    'ukhrul': { d: 'M170 190 L230 185 L310 160 L340 200 L310 240 L250 245 L190 225 Z', cx: 255, cy: 210 },
    'zunheboto': { d: 'M120 160 L170 190 L190 225 L160 260 L110 255 L85 220 L90 200 Z', cx: 140, cy: 218 },
    'kohima': { d: 'M85 220 L110 255 L160 260 L190 295 L150 320 L90 310 L60 270 Z', cx: 120, cy: 278 },
    'phek': { d: 'M190 225 L250 245 L310 240 L320 280 L280 310 L220 310 L190 295 L160 260 Z', cx: 245, cy: 275 },
  };

  return (
    <svg viewBox="0 0 420 340" className="w-full max-w-2xl mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id} onMouseEnter={() => setActiveDistrict(id)} onMouseLeave={() => setActiveDistrict(null)} style={{ cursor: 'pointer' }}>
            <path d={path.d}
              fill={isActive ? '#C75B39' : isHighlight ? 'rgba(199,91,57,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#E89070' : 'rgba(255,255,255,0.3)'} strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }} />
            <text x={path.cx} y={path.cy} textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'} fontSize={isActive ? 11 : 9}
              fontFamily="Georgia, serif" style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}>
              {district?.name}
            </text>
            {isActive && (
              <text x={path.cx} y={path.cy + 15} textAnchor="middle" dominantBaseline="middle"
                fill="#E89070" fontSize={8} fontFamily="Georgia, serif" style={{ pointerEvents: 'none' }}>
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function NagalandStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="nagaland-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="nagaland" />
        </div>
      </nav>

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1712055196088-9bc6da4ffbce?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Nagaland &middot; Land of Festivals</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 16 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where warriors became hosts,<br />and every village is a republic
          </h2>
        </div>
      </section>

      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C75B39] text-[10px] tracking-[0.5em] uppercase mb-6">The Warrior Highlands</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Sixteen tribes, sixteen feasts,<br />
            one mountain fellowship.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            In Longwa village, the chief's house straddles the India-Myanmar border — half in each 
            country, a legacy of lines drawn on maps that meant nothing to the Konyak people. In 
            Kohima, the ridge where the Japanese advance into India was finally halted in 1944 
            overlooks a city built vertically into the hillside. In the Dzükou Valley, a high-altitude 
            meadow accessible only on foot, wild lilies bloom in monsoon mists that the Angami Nagas 
            have protected for centuries. At the Hornbill Festival each December, all sixteen tribes 
            gather in a single field — log-drumming, warrior chants, smoked pork, and rice beer 
            flowing freely. Every Naga village was historically a sovereign republic with its own 
            laws, its own warrior codes, its own way of reading the forest. This is a land where 
            hospitality was never learned — it was born.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C75B39] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              16 tribes. 16 territories. One highland.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <NagalandMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} />
            </div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#C75B39] text-[10px] tracking-[0.4em] uppercase">Region</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division} Nagaland</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-white/40" /><span className="text-white/60 text-sm">{activeData.altitude}</span></div>
                    <div className="flex flex-wrap gap-2">
                      {activeData.valleys.map(v => (<span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a region to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer" onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Nagaland</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#C75B39] to-[#E89070] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C75B39] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">{tier.features.map(f => (<li key={f} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} /><span className="text-white/55 text-sm">{f}</span></li>))}</ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#C75B39] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">GoHimalayan serves as the technology and marketing partner for state tourism departments — bringing every licensed homestay across Nagaland's warrior highlands online and discoverable.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#C75B39] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#C75B39]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(199,91,57,0.05), transparent)' }}>
            <p className="text-[#C75B39] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C75B39]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#C75B39] text-xs">1</span></div>
                <p className="text-white/60 text-sm">Nagaland shares its licensed homestay registry — GoHimalayan digitizes and maps every property across 16 districts and tribal territories</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C75B39]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#C75B39] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into warrior villages, morung communities, and Hornbill Festival homestays</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C75B39]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#C75B39] text-xs">3</span></div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, festival-season analytics, and tribal-tourism development dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Nagaland."
              className="inline-flex items-center gap-3 bg-[#C75B39] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#d97a5e] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#C75B39" />
      <style>{`@keyframes expandBar { from { width: 0; } to { width: 100%; } }`}</style>
    </div>
  );
}
